import { useEffect, useMemo, useRef, useState } from 'react'
import { Target, List } from 'lucide-react'
import { apiGet } from '../../../lib/api.js'
import { apiErrorText } from '../../../lib/apiError.js'
import { usePersistedState } from '../../../lib/persist.js'
import { useMe } from '../../../lib/me.js'
import { HQ_BRANCH_ID, FALLBACK_BRANCH_ID } from '../../../lib/branch.js'
import { MONTHS_TH_SHORT, fmtDate, fmtTime } from '../../../lib/datetime.js'
import { relabelBranch } from '../../../lib/asset.js'
import { bkkTodayISO, trendBuckets, fmtDay, daysBetween } from '../../../lib/implant.js'
import {
  Badge, BranchSelect, DataTable, DateRangePicker, DATE_RANGE_PRESETS, ErrorState, Field, FilterCard,
  Loading, PatientName, Select, StatTile,
} from '../../../kit'
import TrendLine from '../../../components/TrendLine.jsx'
import StarChart from '../../../components/StarChart.jsx'
import RecallPopup from '../../../components/recall/RecallPopup.jsx'
import { Card, fmt, asList } from '../../statistic/statShared.jsx'
import ImplantType from './ImplantType.jsx'
import PatientStatus from './PatientStatus.jsx'
import ResultPill from './ResultPill.jsx'

/* ────────────────────────────────────────────────────────────────────────────
 * ผลการโทร — the LOG BOOK of แท็บรอโทร (Ram, 2026-10-29: "1 รีวิว decides who is suitable,
 * 2 รอโทร is where staff call and give the recall a status, 3 ผลการโทร keeps the result
 * of tab 2 — after the status is given the record goes to tab 3, even Warm Case").
 *
 * Source: GET /api/implant/call-log → Xano api 680. A call is a recall note with an
 * outcome chip; นัดได้ / จ่ายแล้ว are api 679's rules (implant/report.md §6g / §6h).
 *
 *   filters   ช่วงวันที่ (of the calls — the kit DateRangePicker, presets 7 · 30 · 60 วัน ·
 *             ทั้งหมด, or any range; 2026-10-30) · ประเภท (a kit Select like แท็บรอโทร's:
 *             the list the call was made from, Warm Case included, ทั้งหมด by default) · สาขา
 *   filters   … and ผลการโทร (a kit Select at the top, 2026-10-30: ทั้งหมด · นัดได้ · จ่ายแล้ว,
 *             then the five latest-result groups) — it narrows ONLY the log.
 *   card      โทรแล้ว (patients · calls) · รับสาย · นัดได้ · จ่ายแล้ว; the calls per day with
 *             ทำนัดได้ columns; and beside it a star chart of the latest result per patient
 *             (api 680 `groups`). All of these follow the date range, ประเภท and สาขา but
 *             never the ผลการโทร filter (Ram), so they stay put while the log is narrowed;
 *             a click on a tile or a star point sets that filter.
 *   log       ONE ROW PER PATIENT (Ram: "show as patient but can look up every call"):
 *             ประเภท and the patient's status now, the latest result first, its note, the
 *             earlier results as small tags, who
 *             called last and how many times, นัดได้ / จ่ายแล้ว. Every call is also in the
 *             drawer's โน้ต Recall. A click on a tile or a result chip narrows the list.
 *
 * The drawer only READS here: no outcome chips (a call is logged on แท็บรอโทร, under the
 * lock) and no lock buttons — a log never claims a patient away from the callers.
 *
 * Replaced, same day: the Implant Recall card of reviewed patients (api 660 — it could
 * never show Warm Case, who skip review), its patient list, and the Warm Case funnel
 * (api 679 `warm`). api 679 still answers; nothing on this page reads it.
 * ──────────────────────────────────────────────────────────────────────────── */

// The date range's quick picks (Ram, 2026-10-30); ทั้งหมด is the picker's clearable choice.
const RANGE_PRESETS = ['last7', 'last30', 'last60']
const PER_PAGE = 50
// 'YYYY-MM-DD' → epoch ms of 00:00 Bangkok (api 680's start_ms / end_ms)
const bkkMidnight = (iso) => Date.parse(`${iso}T00:00:00+07:00`)

const COLD = '#2563eb'
const BOOKED = '#059669'

// api 680 `result`: 0 ทุกผล · 1 นัดได้ · 2 จ่ายแล้ว · latest result 3-7. Each latest-result
// group keeps its outcome colour (data/implantOutcomes.js OUTCOME_GROUPS) — the star
// chart's spoke dots.
const RESULT_CHIPS = [
  { value: '3', key: 'contact', label: 'ติดต่อได้ ยังไม่พร้อม', color: '#d97706' },
  { value: '4', key: 'noreply', label: 'ไม่รับสาย', color: '#64748b' },
  { value: '7', key: 'bk', label: 'จองแล้ว', color: '#059669' },
  { value: '5', key: 'freeze', label: 'พักจนถอนฟันซี่ใหม่', color: '#0284c7' },
  { value: '6', key: 'cancel', label: 'ยกเลิกเคส', color: '#dc2626' },
]
const RESULT_TITLE = { 1: 'นัดได้', 2: 'จ่ายแล้ว', ...Object.fromEntries(RESULT_CHIPS.map((c) => [c.value, `ผลล่าสุด ${c.label}`])) }

const pct = (a, b) => (Number(b) > 0 ? `${Math.round((Number(a) / Number(b)) * 100)}%` : '—')
const WEEKDAY_TH = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.']
const dayMonth = (iso) => { const [, m, d] = iso.split('-'); return `${Number(d)} ${MONTHS_TH_SHORT[Number(m) - 1]}` }

export default function CallResults() {
  const [branchSaved, setBranchSel] = usePersistedState('imp:res:branch', '')
  // A browser that saved สนามเป้า opens on อารีย์ instead (one branch here).
  const branchSel = Number(branchSaved) === HQ_BRANCH_ID ? String(FALLBACK_BRANCH_ID) : branchSaved
  // The range opens on the last 30 days, every time — a saved fixed range would go stale.
  const today = bkkTodayISO()
  const [range, setRange] = useState(() => {
    const [start, end] = DATE_RANGE_PRESETS.last30.range(bkkTodayISO())
    return { start, end }
  })
  const startMs = range.start ? bkkMidnight(range.start) : 0
  const endMs = range.end ? bkkMidnight(range.end) + 86400000 : 0   // the day after, exclusive
  const presetHit = RANGE_PRESETS.find((k) => {
    const [a, b] = DATE_RANGE_PRESETS[k].range(today)
    return a === range.start && b === range.end
  })
  const rangeLabel = !range.start ? 'ทั้งหมด'
    : presetHit ? `${DATE_RANGE_PRESETS[presetHit].label}ที่ผ่านมา`
      : `${fmtDate(bkkMidnight(range.start))} – ${fmtDate(bkkMidnight(range.end))}`
  const [typeSaved, setType] = usePersistedState('imp:res:type', 'Q')
  const type = ['Q', 'T', 'W', 'A', 'E', 'B', 'G', 'C', 'D', 'F'].includes(typeSaved) ? typeSaved : 'Q'
  const [result, setResult] = useState(0)
  const [page, setPage] = useState(1)
  const [tick, setTick] = useState(0)
  // a filter change goes back to page 1 in the same render (one request, never two)
  const refilter = (setter) => (v) => { setter(v); setPage(1) }

  const key = [branchSel, startMs, endMs, type, result].join('|')
  const [data, setData] = useState(null)
  const [st, setSt] = useState({ loading: true, err: '' })
  const acRef = useRef(null)
  useEffect(() => {
    acRef.current?.abort()
    const ac = new AbortController()
    acRef.current = ac
    setSt({ loading: true, err: '' })
    apiGet('/api/implant/call-log', { branch_id: branchSel || 0, start_ms: startMs, end_ms: endMs, type, result, page, per_page: PER_PAGE }, { signal: ac.signal })
      .then((r) => { setData({ ...r, _key: key }); setSt({ loading: false, err: '' }) })
      .catch((e) => { if (e?.name !== 'AbortError') setSt({ loading: false, err: apiErrorText(e, 'โหลดผลการโทรไม่สำเร็จ') }) })
    return () => ac.abort()
    // key is derived from exactly the filters below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchSel, startMs, endMs, type, result, page, tick])

  // Another filter's rows never sit under this one's title; another page of the same
  // filter stays and dims while the next loads (the kit's table rule).
  const current = data && data._key === key ? data : null
  const rows = asList(current?.rows)
  const total = Number(current?.total_rows) || 0
  const pageTotal = Math.max(1, Math.ceil(total / PER_PAGE))

  // ประเภท — the same kit Select as แท็บรอโทร (Ram, 2026-10-30): ทั้งหมด first, one type,
  // then ถอนฟันแล้ว's ages as a group whose first choice is every age (T). Counted as
  // patients by the list of their latest call (any type).
  const tc = current?.type_counts || data?.type_counts || {}
  const typeCount = (id) => {
    const lists = id === 'Q' ? Object.keys(tc) : id === 'T' ? ['G', 'C', 'D', 'F'] : [id]
    const n = lists.reduce((sum, l) => sum + (Number(tc[l]) || 0), 0)
    return data ? fmt(n) : undefined
  }
  const AGES = [['G', 'ภายใน 1 เดือน'], ['C', '2–6 เดือน'], ['D', '6–12 เดือน'], ['F', '1–2 ปี']]
  const typeOptions = [
    { value: 'Q', label: 'ทั้งหมด', count: typeCount('Q') },
    { value: 'W', label: 'Warm Case', count: typeCount('W') },
    { value: 'L', label: 'ปรึกษาเกิน 6 เดือน', count: typeCount('L') },
    { value: 'A', label: 'มีนัดแล้ว', count: typeCount('A') },
    { value: 'E', label: 'ใส่ฟันปลอม', count: typeCount('E') },
    { value: 'B', label: 'ติดป้ายรากเทียม', count: typeCount('B') },
    { value: 'T', label: 'ทุกช่วง', selectedLabel: 'ถอนฟันแล้ว', count: typeCount('T'), group: 'ถอนฟันแล้ว' },
    ...AGES.map(([v, l]) => ({ value: v, label: l, selectedLabel: `ถอนฟันแล้ว · ${l}`, count: typeCount(v), group: 'ถอนฟันแล้ว' })),
  ]

  const trend = useMemo(() => {
    if (!current) return []
    const daysRows = asList(current.trend).map((r) => ({ d: r.date, calls: r.calls, reached: r.reached, booked: r.booked }))
    // the chart spans the chosen range (ทั้งหมด: from the first call to today)
    const endISO = range.end || today
    const span = range.start ? daysBetween(range.start, endISO) : 0
    return trendBuckets(daysRows, { days: span, today: endISO }).map((b) => ({
      ...b,
      key: b.start,
      label: dayMonth(b.start),
      full: b.weekly
        ? `สัปดาห์ ${dayMonth(b.start)} – ${dayMonth(b.end)}`
        : `${WEEKDAY_TH[new Date(`${b.start}T00:00:00Z`).getUTCDay()]} ${dayMonth(b.start)}`,
    }))
  }, [current, range.start, range.end, today])
  const weekly = trend[0]?.weekly

  // the drawer READS the record — no chips, no lock buttons (see the top)
  const me = useMe()
  const [selected, setSelected] = useState(null)
  const listRef = useRef(null)
  const pickResult = (v) => {
    refilter(setResult)(v)
    requestAnimationFrame(() => listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }
  const groups = current?.groups || data?.groups || {}
  const tiles = current || data
  // ผลการโทร dropdown: everyone, the two outcomes, then the latest-result groups
  const gCount = (k) => (data ? fmt(groups[k] || 0) : undefined)
  const resultOptions = [
    { value: '0', label: 'ทั้งหมด', count: tiles ? fmt(tiles.patients) : undefined },
    { value: '1', label: 'นัดได้', count: tiles ? fmt(tiles.booked) : undefined },
    { value: '2', label: 'จ่ายแล้ว', count: tiles ? fmt(tiles.paid) : undefined },
    ...RESULT_CHIPS.map((c) => ({ value: c.value, label: c.label, selectedLabel: `ผลล่าสุด ${c.label}`, count: gCount(c.key), group: 'ผลล่าสุด' })),
  ]
  const starAxes = RESULT_CHIPS.map((c) => ({ key: c.value, label: c.label, color: c.color, value: Number(groups[c.key]) || 0 }))

  return (
    <>
      <FilterCard compact>
        <Field label="ช่วงวันที่" className="min-w-[240px]">
          <DateRangePicker value={range} onChange={refilter(setRange)} presets={RANGE_PRESETS} clearable placeholder="ทั้งหมด" />
        </Field>
        <Field label="ประเภท" className="min-w-[200px]">
          <Select value={type} onChange={refilter(setType)} options={typeOptions} />
        </Field>
        <Field label="ผลการโทร" className="min-w-[220px]">
          <Select value={String(result)} onChange={(v) => refilter(setResult)(Number(v) || 0)} options={resultOptions} />
        </Field>
        <Field label="สาขา" className="min-w-[160px]">
          <BranchSelect value={branchSel} onChange={refilter(setBranchSel)} />
        </Field>
      </FilterCard>

      {/* 1 · the numbers and the calls per day — they follow the filters above */}
      <div className="mt-4">
        <Card icon={<Target size={18} />} title="Implant Recall"
          subtitle={`ผลการโทรจากแท็บรอโทร · ${rangeLabel}${st.loading && data ? ' · กำลังอัปเดต…' : ''}`}>
          {st.err && !tiles ? (
            <ErrorState title="โหลดผลการโทรไม่สำเร็จ" message={st.err} onRetry={() => setTick((t) => t + 1)} />
          ) : !tiles ? (
            <Loading />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                <StatTile label="โทรแล้ว" value={`${fmt(tiles.patients)} ราย`} sub={`${fmt(tiles.calls)} ครั้ง`}
                  onClick={() => pickResult(0)} active={result === 0} />
                <StatTile label="รับสาย" value={`${fmt(tiles.reached)} ครั้ง`} sub={`${pct(tiles.reached, tiles.calls)} ของการโทร`} />
                <StatTile label="นัดได้" value={`${fmt(tiles.booked)} ราย`} tone="good" sub={`${pct(tiles.booked, tiles.patients)} ของคนที่โทร`}
                  onClick={() => pickResult(1)} active={result === 1} />
                <StatTile label="จ่ายแล้ว" value={`${fmt(tiles.paid)} ราย`} tone="good" sub="บิลหลังการโทรครั้งแรก"
                  onClick={() => pickResult(2)} active={result === 2} />
              </div>
              {/* the calls over time, and beside it what they came to — both follow the date
                  range, ประเภท and สาขา, never the ผลการโทร filter */}
              <div className="mt-5 grid gap-x-8 gap-y-6 border-t border-slate-100 pt-4 lg:grid-cols-[minmax(0,1.75fr)_minmax(300px,1fr)]">
                <div className="min-w-0">
                  <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                    <h3 className="text-[13px] font-semibold text-slate-700">{weekly ? 'การโทรรายสัปดาห์' : 'การโทรรายวัน'}</h3>
                    <span className="text-[12px] text-slate-400">
                      โทร {fmt(tiles.calls)} ครั้ง · ทำนัดได้ {fmt(tiles.booked)} ราย · {rangeLabel}
                    </span>
                  </div>
                  <TrendLine data={trend} emptyText="ยังไม่มีการโทรในช่วงนี้"
                    line={{ key: 'calls', label: 'โทร', color: COLD, unit: 'ครั้ง' }}
                    bar={{ key: 'booked', label: 'ทำนัดได้', color: BOOKED, unit: 'ราย' }} />
                </div>
                <div className="min-w-0">
                  <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                    <h3 className="text-[13px] font-semibold text-slate-700">ผลล่าสุดของคนที่โทร</h3>
                    <span className="text-[12px] text-slate-400">{fmt(tiles.patients)} ราย · กดจุดเพื่อดูรายชื่อ</span>
                  </div>
                  <StarChart axes={starAxes} color={COLD} unit="ราย" height={240} emptyText="ยังไม่มีการโทรในช่วงนี้"
                    activeKey={result >= 3 ? String(result) : undefined} onPick={(k) => pickResult(Number(k))} />
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* 2 · the log — one row per patient, latest result first */}
      <div ref={listRef} className="mt-4 scroll-mt-4">
        <Card icon={<List size={18} />}
          title={`บันทึกผลการโทร${result ? ` · ${RESULT_TITLE[result]}` : ''}`}
          badge={current ? `${fmt(total)} ราย` : undefined}
          subtitle="คนละ 1 แถว ผลล่าสุดขึ้นก่อน · ผลครั้งก่อน ๆ อยู่ใต้ผลล่าสุด และทุกครั้งอยู่ในโน้ต Recall ของหน้าต่างคนไข้ · กดชื่อเพื่อเปิดหน้าเต็ม"
          right={result ? (
            <button type="button" onClick={() => pickResult(0)} className="text-[12.5px] font-medium text-brand-700 hover:underline">ดูทั้งหมด</button>
          ) : undefined}>
          <DataTable columns={COLUMNS} rows={rows} rowKey={(r) => r.patient_id} valign="top" fixed minWidth={1000}
            onRowClick={setSelected}
            rowClassName={(r) => (selected && String(r.patient_id) === String(selected.patient_id) ? 'bg-brand-50/60' : '')}
            loading={st.loading} error={st.err || undefined} onRetry={() => setTick((t) => t + 1)}
            empty={{ title: 'ยังไม่มีผลการโทรในช่วงนี้', hint: 'ผลการโทรที่บันทึกในแท็บรอโทรจะขึ้นที่นี่' }}
            page={page} pageTotal={pageTotal} onPageChange={setPage} total={current ? total : undefined} />
        </Card>
      </div>

      {selected && (
        <RecallPopup
          key={selected.patient_id}
          fullPageTab="treatments"
          patient={selected}
          me={me}
          onClose={() => setSelected(null)}
          onChanged={() => setTick((t) => t + 1)}
          refreshKey={tick}
        />
      )}
    </>
  )
}

const EARLIER_SHOWN = 4

/* Fixed widths (Ram, 2026-10-30), as แท็บรอโทร: in the auto layout a long note or
 * caller name stretched its column until นัด / จ่าย ran past the card. Every column but
 * คนไข้ has its width; what is longer truncates (the full text in its tooltip). */
const COLUMNS = [
  {
    key: 'patient', label: 'คนไข้',
    render: (r) => (
      <PatientName patient={{ id: r.patient_id, name: r.name, hn: r.hn }} avatar nickname={r.nickname}
        meta={[r.age != null ? `${r.age} ปี` : null, relabelBranch(r.branch)].filter(Boolean).join(' · ')} />
    ),
  },
  { key: 'type', label: 'ประเภท', width: '132px', render: (r) => <ImplantType list={r.last_list} /> },
  // patient.status now (Ram, 2026-10-30) — the same pill as แท็บรอโทร
  { key: 'status', label: 'สถานะ', width: '150px', render: (r) => <PatientStatus status={r.patient_status} /> },
  {
    // the latest result and its note; every earlier call as a small tag (date · who in
    // its tooltip), oldest first, the last few shown
    key: 'result', label: 'ผลล่าสุด', width: '240px',
    render: (r) => {
      const trail = asList(r.trail)
      const earlier = trail.slice(0, -1)
      const shown = earlier.slice(-EARLIER_SHOWN)
      return (
        <>
          <ResultPill code={r.last_code} />
          {r.last_note && <span className="mt-1 block truncate text-[12px] text-slate-500" title={r.last_note}>{r.last_note}</span>}
          {earlier.length > 0 && (
            <span className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-slate-400">
              ก่อนหน้า
              {earlier.length > shown.length && <span>+{earlier.length - shown.length}</span>}
              {shown.map((c, i) => (
                <ResultPill key={i} small code={c.code}
                  title={`${fmtDate(c.at)} ${fmtTime(c.at)}${c.by ? ` · ${c.by}` : ''}${c.note ? ` — ${c.note}` : ''}`} />
              ))}
            </span>
          )}
        </>
      )
    },
  },
  {
    key: 'last', label: 'โทรล่าสุด', width: '150px',
    render: (r) => (
      <>
        <span className="block whitespace-nowrap tabular-nums">{fmtDate(r.last_at)} <span className="text-slate-400">{fmtTime(r.last_at)}</span></span>
        {/* a long caller name truncates; the count stays */}
        <span className="flex gap-1 whitespace-nowrap text-[12px] text-slate-400">
          {r.last_by_code && <span className="truncate" title={r.last_by_code}>โดย {r.last_by_code} ·</span>}
          <span className="shrink-0">โทร {fmt(r.calls_n)} ครั้ง</span>
        </span>
      </>
    ),
  },
  {
    key: 'outcome', label: 'นัด / จ่าย', width: '132px',
    render: (r) => (r.booked_at || r.paid_date ? (
      <span className="flex flex-col items-start gap-1">
        {r.booked_at && <Badge tone="emerald">นัดได้ {fmtDate(r.booked_at)}</Badge>}
        {r.paid_date && <Badge tone="emerald">จ่ายแล้ว {fmtDay(r.paid_date)}</Badge>}
      </span>
    ) : <span className="text-slate-300">—</span>),
  },
]
